import type Shipment            from '@/domains/Shipments/Shipment';
import type Product             from '@/domains/Products/Product';
import OrderPosition       from "@/domains/Orders/OrderPosition";
import Title_ru_RU              from '@/core/misc/decorators/Title_ru_RU';
import Title_ru_RU_Class        from "@/core/misc/decorators/Title_ru_RU_Class";
import serializeSimplified      from "@/core/misc/decorators/SerializeSimplified";
import TYPES                    from '@/core/services/Changelogs/types';
import AbstractDocumentPosition from '@/domains/Abstract/AbstractDocumentPosition';
import Core                     from '@/core/Core';
import ValidationError          from '@/core/misc/ValidationError';
import DateSimple 		        from '@/core/types/DateSimple';
import Unit 					from '@/domains/Units/Unit';
import Qty                      from '@/core/types/Qty';
import Numeric                  from '@/core/types/Numeric';
import Price                    from '@/core/types/Price';
import Sum                      from '@/core/types/Sum';
import CurrencyRate             from '@/core/types/CurrencyRate';
import Currency                 from '@/domains/Currencies/Currency';
import CurrenciesService        from '@/domains/Currencies/CurrenciesService';
import AssemblyPosition         from "@/domains/Assemblies/AssemblyPosition";
import type Company             from "@/domains/Companies/Company";
import VatRate                  from "@/core/types/VatRate";

/**
 * Позиция отгрузки
 */
@Title_ru_RU_Class('Позиция отгрузки')
class ShipmentPosition extends AbstractDocumentPosition {

    private _shipment: Shipment;

    // по идее суффикс Seller тут надо повыкидывать
    private _assemblyPosition: AssemblyPosition | null;

    // ---- Количество ----

    @Title_ru_RU('Количество принятого', TYPES.NUMBER)
    public qtyReceived: Qty;

    // ---- Услуга ----

    public product: Product
    public unit: Unit

    /** Чисто фронтовое свойство, которое будет заполнено лишь в случае получения позиции с сервера.
     * Тут лежит значение к отгрузке, до начала редактирования.
     *
     * Нужно завести поле, и не лазить в orderPosition, потому что в OrderPosition лежит
     * полная информация о всех отгрузках и сборках которые когда-либо совершались с позицией, а нам нужно
     * значение лишь в пределах этого документа */
    public originQtyShipped: Qty;

    // ---- Валюта сделки ----

    protected _orderCurrencyCode: string | null;
    protected _orderCurrency: Currency | null;

    /* Цена покупателя */
    public price: Price; // OrderPosition.priceSeller

    /* Ставка НДС */
    public vatRate: VatRate | null; // OrderPosition.vatRateSeller

    /* НДС включен или нет */
    public isVatIncluded: boolean | null; // OrderPosition.isVatIncludedSeller

    /**
     * Цена с учетом НДС. Поле СПРАВОЧНОЕ: при «НДС сверху» использовать его как
     * основу расчёта нельзя (теряется точность), при «НДС в т.ч.» оно не нужно.
     * Считается и хранится только для отображения и контракта с бэком.
     */
    public priceFull: Price; // OrderPosition.priceFullSeller

    /* Сумма поставщика */
    @Title_ru_RU('Сумма в валюте сделки', TYPES.SUM)
    public sum: Sum = Sum.zero();

    /* Полная сумма, с учетом НДС */
    @Title_ru_RU('Полная сумма в валюте сделки', TYPES.SUM)
    public sumFull: Sum = Sum.zero();

    /* Сумма НДС */
    @Title_ru_RU('НДС в валюте сделки', TYPES.SUM)
    public vatSum: Sum = Sum.zero();

    // ---- Валюта учёта ----

    protected _accountingCurrencyCode: string | null = Core.currentPartner.country.localCurrency.code || Core.config.localCurrency.code;
    protected _accountingCurrency: Currency | null;

    /* Курс валюты учета по отношению к валюте заказа */
    private _accountingCurrencyRate: CurrencyRate; // shipment.accountingCurrencyRate

    // ---- Поля под ввод пользователя (без суффикса Calculated) ----
    // Инициализируются значениями *Calculated, дальше живут своей жизнью:
    // пользователь вправе переопределить любое.

    /* Цена покупателя в валюте учёта (ручная) */
    @Title_ru_RU('Цена в валюте учёта', TYPES.PRICE)
    private _accountingPrice: Price;

    /* Цена с учетом НДС в валюте учёта. Справочное — см. priceFull. */
    public accountingPriceFull: Price;

    /* Сумма поставщика в валюте учёта */
    public accountingSum: Sum;

    /* Полная сумма, с учетом НДС в валюте учёта (ручная) */
    @Title_ru_RU('Полная сумма в валюте учёта', TYPES.SUM)
    private _accountingSumFull: Sum;

    /* Сумма НДС в валюте учёта (ручная) */
    @Title_ru_RU('НДС в валюте учёта', TYPES.SUM)
    private _accountingVatSum: Sum;

    // ---- ...Calculated — расчёт (фронт/бэк), эталон для инициализации полей ввода ----

    /* Цена покупателя в валюте учёта расчётная */
    @Title_ru_RU('Цена в валюте учёта (рассчитанная автоматически)', TYPES.PRICE)
    private _accountingPriceCalculated: Price;

    /** Геттер на это свойство существует только для получения с бека этого поля,
     * потому что сериализатор так работает */
    @Title_ru_RU('Полная цена в валюте учёта (рассчитанная автоматически)', TYPES.PRICE)
    private _accountingPriceFullCalculated: Price;

    /* Сумма поставщика в валюте учёта расчётная */
    @Title_ru_RU('Сумма в валюте учёта (рассчитанная автоматически)', TYPES.SUM)
    private _accountingSumCalculated: Sum;

    /* Полная сумма, с учетом НДС в валюте учёта расчётная */
    @Title_ru_RU('Полная сумма в валюте учёта (рассчитанная автоматически)', TYPES.SUM)
    private _accountingSumFullCalculated: Sum;

    /* Сумма НДС в валюте учёта расчётная */
    @Title_ru_RU('НДС в валюте учёта (рассчитанная автоматически)', TYPES.SUM)
    private _accountingVatSumCalculated: Sum;

    // ---- Плановая дата оплаты ----

    @Title_ru_RU('Дата оплаты', TYPES.DATE)
    public plannedPaymentDate: DateSimple;

    /* ────────────────────────────────────────
       Type guard
       ──────────────────────────────────────── */

    static isShipmentPosition(entity: unknown): entity is ShipmentPosition {
        return entity instanceof ShipmentPosition;
    }

    /* ────────────────────────────────────────
       fromServer
       ──────────────────────────────────────── */

    static fromServer(data: any, result: ShipmentPosition = new ShipmentPosition()): ShipmentPosition {
        super.fromServer(data, result);

        // ---- Позиция сборки ----
        // super разлил assemblyPosition сырьём по имени. Позиция усечённая —
        // большинство полей null, но qty/qtyShipped приходят числами и нужны
        // для isShippedEntirely/qtyReminder. Numeric-типы автомат не подхватывает,
        // гидрируем их по месту. Позиции сборки может не быть (свободная отгрузка).
        if (Core.utils.isObject(result._assemblyPosition)) {
            result._assemblyPosition.qty        = Core.utils.hydrate(result._assemblyPosition.qty, Qty.fromServer)!;
            result._assemblyPosition.qtyShipped = Core.utils.hydrate(result._assemblyPosition.qtyShipped, Qty.fromServer)!;
        }

        result.orderPosition = Core.utils.hydrate(data.orderPosition, OrderPosition.fromServer)!;

        // ---- Количества ----
        result.qtyReceived      = Core.utils.hydrate(data.qtyReceived, Qty.fromServer)!;
        /** У услуги позиции заказа нет, отгружать по ней нечего */
        if (Core.utils.isNullOrUndefined(result.orderPosition) === false) {
            result.originQtyShipped = result.orderPosition.shipmentStatus.shipped;
        }

        // ---- Валюта сделки ----
        // super положил _orderCurrencyCode напрямую, минуя сеттер — _orderCurrency
        // не подтянут. Прогоняем через сеттер, чтобы он взял объект из словаря.
        result.orderCurrencyCode = result._orderCurrencyCode;

        result.price     = Core.utils.hydrate(data.price, Price.fromServer)!;
        result.vatRate   = Core.utils.hydrate(data.vatRate, VatRate.fromServer)!;
        result.priceFull = Core.utils.hydrate(data.priceFull, Price.fromServer)!;
        result.sum       = Core.utils.hydrate(data.sum, Sum.fromServer)!;
        result.sumFull   = Core.utils.hydrate(data.sumFull, Sum.fromServer)!;
        result.vatSum    = Core.utils.hydrate(data.vatSum, Sum.fromServer)!;

        // ---- Валюта учёта ----
        result.accountingCurrencyCode = result._accountingCurrencyCode;

        result._accountingCurrencyRate = Core.utils.hydrate(data.accountingCurrencyRate, CurrencyRate.fromServer)!;

        // Поля под ввод пользователя — с бэка кладём напрямую в _protected,
        // минуя сеттеры: сеттер дёрнул бы пересчёт на ещё не заполненных соседях.
        result._accountingPrice    = Core.utils.hydrate(data.accountingPrice, Price.fromServer)!;
        result.accountingPriceFull = Core.utils.hydrate(data.accountingPriceFull, Price.fromServer)!;
        result.accountingSum       = Core.utils.hydrate(data.accountingSum, Sum.fromServer)!;
        result._accountingSumFull  = Core.utils.hydrate(data.accountingSumFull, Sum.fromServer)!;
        result._accountingVatSum   = Core.utils.hydrate(data.accountingVatSum, Sum.fromServer)!;

        // Расчётные — бэк считает их сам, берём как истину
        result._accountingPriceCalculated        = Core.utils.hydrate(data.accountingPriceCalculated, Price.fromServer)!;
        result._accountingPriceFullCalculated    = Core.utils.hydrate(data.accountingPriceFullCalculated, Price.fromServer)!;
        result._accountingSumCalculated          = Core.utils.hydrate(data.accountingSumCalculated, Sum.fromServer)!;
        result._accountingSumFullCalculated      = Core.utils.hydrate(data.accountingSumFullCalculated, Sum.fromServer)!;
        result._accountingVatSumCalculated       = Core.utils.hydrate(data.accountingVatSumCalculated, Sum.fromServer)!;

        result.plannedPaymentDate = Core.utils.hydrate(data.plannedPaymentDate, DateSimple.fromServer)
        return result;
    }

    /* ────────────────────────────────────────
       Фабричные методы
       ──────────────────────────────────────── */

    static fromAssemblyPosition(assemblyPosition: AssemblyPosition, result: ShipmentPosition = new ShipmentPosition()): ShipmentPosition {
        this.fromOrderPosition(assemblyPosition.orderPosition, result);

        result._assemblyPosition = assemblyPosition;

        /** При создании позиции отгрузки из сборки — сколько осталось отгрузить:
         * из общего количества собранного вычесть уже отгруженное.
         * Округление не нужно — Qty нормализован до своего scale конструктором.
         *
         * Пишем мимо сеттера: он тянет пересчёт сумм по документу, а документа
         * ещё нет. Пересчёт делает Shipment.addPosition. */
        result._qty = assemblyPosition.qty.minus(assemblyPosition.qtyShipped);

        return result;
    }

    /**
     * Позиция отгрузки из позиции заказа.
     * Ссылку на отгрузку и зависящие от неё поля ставит Shipment.addPosition —
     * фабрика собирает позицию саму по себе.
     */
    static fromOrderPosition(orderPosition: OrderPosition, result: ShipmentPosition = new ShipmentPosition()): ShipmentPosition {
        super.fromOrderPosition(orderPosition, result);

        result.orderCurrencyCode = orderPosition.currencyCode;

        result.vatRate       = orderPosition.vatRateSeller!;
        result.price         = orderPosition.priceSeller;
        result.isVatIncluded = orderPosition.isVatIncludedSeller;
        result.priceFull     = orderPosition.priceFullSeller;

        return result;
    }

    /** в данном случае product.isService = true - услуга */

    static fromServiceProduct(product: Product, result: ShipmentPosition = new ShipmentPosition()): ShipmentPosition {
        const position = result;

        position.product = product;

        /** шт по умолчанию, могут быть часы, но маловероятно */

        position.unit = Unit.initDefault() as Unit;

        /** ставка по умолчанию, признак «НДС включён» ставит Shipment.addService по документу */

        position.vatRate = VatRate.fromServer(Core.config.localDefaultVatRate);

        position.price     = Price.zero();
        position.priceFull = Price.zero();

        /** Валюта сделки и курс учёта берутся из документа — их проставляет
         *  Shipment.addService, когда услуга в него попадает. */

        /** раз добавили, то 1 шт как минимум. Мимо сеттера — пересчёт
         *  делает Shipment.addService, когда услуга попадёт в документ. */

        position._qty = new Qty(1);

        return position;
    }

    /* ────────────────────────────────────────
       Геттеры / сеттеры
       ──────────────────────────────────────── */

    // ---- shipment ----

    @serializeSimplified
    get shipment(): Shipment { return this._shipment; }

    /** Привязка к родительскому документу Shipment.
     * Только ссылка: инициализация валюты/курса и пересчёт — забота
     * Shipment.addPosition, иначе присваивание ссылки тянуло бы за собой
     * каскад на ещё не заполненной позиции (фабрики) и затирало бы
     * значения, пришедшие с бэка (гидрация). */
    set shipment(shipment: Shipment) {
        this._shipment = shipment;
    }

    // ---- orderPosition ----

    @serializeSimplified

    // ---- assemblyPosition ----

    get assemblyPosition(): any { return this._assemblyPosition; }

    // ---- qty ----

    get qty(): Qty { return this._qty; }

    /** Смена количества тянет пересчёт сумм позиции; суммы документа обновляет
     *  сам calculateSum последним шагом. */
    set qty(qty: Qty) {
      this._qty = qty;
      this.calculateSum();
    }

    /** Максимальное количество к отгрузке */
    get qtyReminder(): Qty {
      /** Если отгрузка совершается не на основе сборочного листа - значит она свободная, значит
       * остаток нужно брать из inWork */
      if (this.shipment.isShipmentByAssembly === false) {
        /**
         * если у неё нет айдишника, то значит она только созданная, а значит максимальное количество
         * это в inWork, Если же айдишник есть, то максимальное количество
         * это inWork + количество до начала редактирования originQtyShipped
         */
        if (this.id !== null && this.id !== undefined) {
          return this.orderPosition.shipmentStatus.inWork.plus(this.originQtyShipped);
        }
        return this.orderPosition.shipmentStatus.inWork;
      }

      /** availableQty - количество которое еще в позиции сборки осталось отгрузить */
      const availableQty = this.assemblyPosition.qty.minus(this.assemblyPosition.qtyShipped);
      if (this.id !== null && this.id !== undefined) {
        /** В случае редактирования нужно сложить со значением, на момент до редактирования (originQtyShipped) */
        return availableQty.plus(this.originQtyShipped);
      }
      return availableQty;
    }

    /**
     * Позиция отгружена полностью — имеет смысл только для отгрузок по сборке.
     * В обычных (не по сборке) отгрузках геттер всегда возвращает false.
     * Позиции сборки может не быть (null/undefined) — тогда сравнивать не с чем,
     * возвращаем false, не доходя до equals (иначе equals(undefined) кинет).
     */
    get isShippedEntirely(): boolean {
      if (this.shipment.isShipmentByAssembly === false) {
        return false;
      }
      else if (Core.utils.isObject(this.assemblyPosition) === false) {
        return false;
      }
      else {
        return this.qty.equals(this.assemblyPosition.qty);
      }
    }

    // ---- Валюта сделки ----
    // Пара код/объект: код ведущий, объект подтягивается из словаря — снаружи
    // удобно манипулировать объектом (v-model на VSelect), контракт с бэком по коду.

    get orderCurrencyCode(): string | null { return this._orderCurrencyCode; }
    set orderCurrencyCode(value: string | null) {
        this._orderCurrencyCode = value;

        if (this._orderCurrencyCode) {
            this._orderCurrency = CurrenciesService.currencies.get(this._orderCurrencyCode);
        } else {
            this._orderCurrency = null;
        }
    }

    get orderCurrency(): Currency | null { return this._orderCurrency; }
    set orderCurrency(value: Currency | null) {
        this._orderCurrency = value;
        this.orderCurrencyCode = this._orderCurrency?.code ?? null;
    }

    // ---- Валюта учёта ----

    get accountingCurrencyCode(): string | null { return this._accountingCurrencyCode; }
    set accountingCurrencyCode(value: string | null) {
        this._accountingCurrencyCode = value;

        if (this._accountingCurrencyCode) {
            this._accountingCurrency = CurrenciesService.currencies.get(this._accountingCurrencyCode);
        } else {
            this._accountingCurrency = null;
        }
    }

    get accountingCurrency(): Currency | null { return this._accountingCurrency; }
    set accountingCurrency(value: Currency | null) {
        this._accountingCurrency = value;
        this.accountingCurrencyCode = this._accountingCurrency?.code ?? null;
    }

    // ---- accountingPrice ----

    get accountingPrice(): Price { return this._accountingPrice; }

    set accountingPrice(value: Price) {
        this._accountingPrice = value;
        this.calculateAccounting();
        this._shipment.calculateDependentSums();
    }

    // ---- accountingCurrencyRate ----

    get accountingCurrencyRate(): CurrencyRate { return this._accountingCurrencyRate; }

    set accountingCurrencyRate(currencyRate: CurrencyRate) {
        this._accountingCurrencyRate = currencyRate;
        this.calculateAccounting();
    }

    // ---- accountingVatSum ----

    get accountingVatSum(): Sum { return this._accountingVatSum; }

    set accountingVatSum(value: Sum) {
        this._accountingVatSum = value;
        this._shipment.calculateDependentSums();
    }

    // ---- accountingSumFull ----

    get accountingSumFull(): Sum { return this._accountingSumFull; }

    set accountingSumFull(value: Sum) {
        this._accountingSumFull = value;
        this._shipment.calculateDependentSums();
    }

    // ---- Calculated геттеры ----

    get accountingPriceCalculated(): Price { return this._accountingPriceCalculated; }

    /** Геттер на это свойство существует только для получения с бека этого поля,
     * потому что сериализатор так работает */
    get accountingPriceFullCalculated(): Price { return this._accountingPriceFullCalculated; }

    get accountingSumCalculated(): Sum { return this._accountingSumCalculated; }
    get accountingSumFullCalculated(): Sum { return this._accountingSumFullCalculated; }
    get accountingVatSumCalculated(): Sum { return this._accountingVatSumCalculated; }

    /* ────────────────────────────────────────
       Валидация
       ──────────────────────────────────────── */

    /**
     * К базовой проверке (availableQty * 2, кратность упаковки, минимум по отгруженному)
     * добавляется правило: если превышен сам остаток к отгрузке —
     * предупреждаем «Превышен максимум на N ед.».
     *
     * Полностью отгруженные позиции проверке не подлежат.
     */
    validatePositionQty(value: Qty): string | null {
      if (this.isShippedEntirely === true) {
        return null;
      }
      else {
        const baseResult = super.validatePositionQty(value, this.qtyReminder);

        if (baseResult !== null) {
          return baseResult;
        }
        else if (value.greaterThan(this.qtyReminder)) {
          const excess = value.minus(this.qtyReminder);
          const unit = this.orderPosition.unit?.name ?? '';
          return `Превышен максимум на ${excess.format()} ${unit}`;
        }
        else {
          return null;
        }
      }
    }

    /** Валидация НДС-совместимости компании-продавца и позиции заказа.
     * Бывают ситуации, когда пользователь хочет создать документ из
     * неконсистентных данных — юрлицо имеет тип налогообложения,
     * не комплиментарный позициям из которых создаётся документ */
    /**
     * Согласованность НДС позиции и юрлица продавца в отгрузке.
     * Зовётся из Shipment.validatePosition: сравнивать позицию с документом
     * можно только когда документ известен.
     */
    static validateVatConsistency(orderPosition: OrderPosition, shipmentSellerCompany: Company): void {
        let error: string | null = null;

        /** Ставка — семантический тип: «не указана» это null, а не отсутствие
         *  числа. Нулевая ставка указана и означает освобождение от НДС. */
        const hasVatRate = orderPosition.vatRateSeller !== null
            && orderPosition.vatRateSeller.isZero() === false;

        if (shipmentSellerCompany.isVatPayer === false) {
            if (hasVatRate) {
                error = 'Компания продавец не платит НДС, хотя у позиции указана НДС ставка';
            }

            if (typeof orderPosition.isVatIncludedSeller === 'boolean' && hasVatRate) {
                error = 'Компания продавец не платит НДС, хотя у позиции указан тип НДС включён/сверху';
            }
        } else if (shipmentSellerCompany.isVatPayer === true) {
            if (orderPosition.vatRateSeller === null) {
                error = 'Компания продавец платит НДС, хотя у позиции не указана НДС ставка';
            }

            if (orderPosition.isVatIncludedSeller === null) {
                error = 'Компания продавец платит НДС, хотя у позиции не указан тип НДС включён/сверху';
            }
        }

        if (error) {
            throw new ValidationError(error);
        }
    }

    /* ────────────────────────────────────────
       Пересчёт сумм в валюте сделки
       ──────────────────────────────────────── */

    calculateSum(): void {
        this.sum = this.price.mul(this.qty);

        this.calculatePriceFull();
        this.calculateSumFull();
        this.calculateVatSum();

        this.calculateAccounting();
        this._shipment.calculateDependentSums();
    }

    calculatePriceFull(): void {
        this.priceFull = this.getFullPrice(this.price);
    }

    calculateSumFull(): void {
        this.sumFull = this.getFullSum(this.sum);
    }

    calculateVatSum(): void {
        this.vatSum = this.getVatSum(this.sum);
    }

    /* ────────────────────────────────────────
       Пересчёт сумм в валюте учёта
       ──────────────────────────────────────── */

    /**
     * Сделка в локальной валюте: валюта сделки совпадает с валютой учёта.
     * Курс не участвует — суммы учёта равны суммам сделки.
     */
    get isLocalCurrency(): boolean {
        return this._accountingCurrencyCode === this._orderCurrencyCode;
    }

    /**
     * Расчёт группы accounting*Calculated.
     *
     * Введённое пользователем значение приоритетно: если он поправил цену или
     * сумму руками, дальше считаем от неё, а не от расчётной.
     *
     * Локальная валюта — считаем слева направо, как валюту сделки: цена
     * первична, сумма из неё. Курс не нужен, цена учёта равна цене сделки.
     *
     * Разные валюты — направление обратное: первична СУММА (введённая либо
     * пересчитанная по курсу), от неё считаются налог и производная цена.
     * Умножать цену на курс и потом на количество нельзя: округление цены до
     * scale размножилось бы на всё количество. Это свойство мультивалютного
     * УПД — цена производна, обратной сходимости цена × количество = сумма
     * в общем случае нет.
     */
    calculateAccounting(): void {
        /** Курс приезжает асинхронно: до его появления суммы в валюте учёта
         *  не из чего считать. Пересчёт вызовет сеттер accountingCurrencyRate. */
        if (this.isLocalCurrency === false && Core.utils.isNullOrUndefined(this._accountingCurrencyRate)) {
            return;
        }

        if (this.isLocalCurrency) {
            // Цена первична. Если пользователь ввёл свою — считаем от неё.
            this._accountingPriceCalculated = this._accountingPrice ?? this.price;
            this._accountingSumCalculated   = this._accountingPriceCalculated.mul(this.qty);
        }
        else {
            // Пляшем от суммы. Если пользователь ввёл свою — от неё, иначе по курсу.
            this._accountingSumCalculated   = this.accountingSum ?? this.sum.mul(this._accountingCurrencyRate);
            this._accountingPriceCalculated = this._accountingSumCalculated.div(this.qty);
        }

        // Сумма-ввод перебивает расчётную и для налога, и для полной суммы.
        const baseSum = this.accountingSum ?? this._accountingSumCalculated;

        this._accountingVatSumCalculated  = this.getVatSum(baseSum);
        this._accountingSumFullCalculated = this.getFullSum(baseSum);

        this._accountingPriceFullCalculated = this.getFullPrice(this._accountingPriceCalculated);
    }

    /* ────────────────────────────────────────
       Общие формулы НДС (одни на обе валюты)
       ──────────────────────────────────────── */

    /**
     * Полная сумма: НДС включён — сумма и есть полная, иначе накручиваем сверху.
     */
    private getFullSum(sum: Sum): Sum {
      if (this.vatRate === null) {
        return sum;
      }
      else if (this.isVatIncluded === true) {
        return sum;
      }
      else {
        return sum.plus(sum.getVat(false, this.vatRate));
      }
    }

    /**
     * Полная цена. Справочная величина (см. priceFull) — как основа расчёта
     * не используется.
     */
    private getFullPrice(price: Price): Price {
      if (this.vatRate === null) {
        return price;
      }
      else if (this.isVatIncluded === true) {
        return price;
      }
      else {
        return price.addVat(this.vatRate);
      }
    }

    /** Сумма НДС от суммы — «изнутри» либо «сверху» по флагу. */
    private getVatSum(sum: Sum): Sum {
      /** Ставка и флаг включённости приходят парой: нет одного — НДС не начисляется. */
      if (this.vatRate === null || this.isVatIncluded === null) {
        return Sum.zero();
      }
      else {
        return sum.getVat(this.isVatIncluded, this.vatRate);
      }
    }

    /**
     * Валидация отклонения от расчётного значения.
     * Вынесено из шаблона, так как для validatePriceAccounting / validateVatSumAccounting /
     * validateSumFullAccounting логика одинаковая + использование в более, чем одном шаблонах.
     *
     * value и calculatedValue — Numeric (Price/Sum). Сравнение отклонения —
     * UX-проверка «пользователь ввёл близко к расчёту», точность Decimal здесь
     * не нужна, поэтому разворачиваем в number. Вывод расчётного значения —
     * через format() своего типа, precision снаружи не задаётся (это scale типа).
     *
     * @param value           - введённое значение
     * @param calculatedValue - расчётный эталон
     */
    static validateAccountingValue(value: Numeric, calculatedValue: Numeric): string | null {
        if (calculatedValue === null || calculatedValue === undefined || calculatedValue.isZero()) {
            return null;
        }
        else if (!Core.utils.number.isCloseTo(value.toNumber(), calculatedValue.toNumber())) {
            return `Отклонение от ${calculatedValue.format()} более чем на 1%`;
        }
        else {
            return null;
        }
    }
}

export default ShipmentPosition;
